package com.elan.edubridge.ui.member

import com.elan.edubridge.shared.domain.model.AttendanceStatus
import com.elan.edubridge.shared.domain.model.AttendanceTimelineEntry
import com.elan.edubridge.shared.domain.model.ClassData
import com.elan.edubridge.shared.domain.model.Permission
import com.elan.edubridge.shared.domain.model.SessionState
import com.elan.edubridge.ui.shell.escapeHtml
import com.elan.edubridge.ui.shell.formatKstClockTime
import com.elan.edubridge.ui.shell.icon
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import kotlin.js.Date

/**
 * `/app/me` — 오늘 상태 카드 + 등원→수업→하원 타임라인(시안 v4 모바일 ① 정보 구조, 가이드 §3.7),
 * 다음 수업, 미읽음 공지·과제 배지. 로그인한 본인 기준으로 그린다.
 */
object MyTodayPage {
    private val scope = MainScope()

    fun render(session: SessionState) {
        scope.launch {
            val context = loadMemberContext(session)
            MemberShell.render(context, MemberShell.Tab.HOME, body(context))
        }
    }

    private suspend fun body(context: MemberContext): String {
        val today = todayServiceDate()
        val todaysEntries = context.timelineUseCase(
            context.session,
            academyId = null,
            subjectStudentId = null,
            limit = 20,
        ).filter { it.serviceDate == today }

        val checkIn = todaysEntries.firstOrNull {
            it.status == AttendanceStatus.PRESENT || it.status == AttendanceStatus.LATE
        }
        val checkOut = todaysEntries.firstOrNull { it.status == AttendanceStatus.LEFT }

        val academyId = context.subjectAcademyId()
        val subjectUserId = context.subjectStudentUserId()
        // 홈의 "다음 수업" 위젯. 못 읽으면 다음 수업이 없는 것처럼 보이지만, 홈은 실패 화면으로
        // 통째로 덮을 자리가 아니다 — 위젯이 스스로 빠지고 전체 기록은 시간표 화면이 진다(#30).
        val schedule = if (academyId != null && subjectUserId != null) {
            context.repository.studentScheduleResult(academyId, subjectUserId).items
        } else {
            emptyList()
        }
        val next = nextClassOccurrence(schedule)

        val notifications = MemberNotifications.loadAll(context)
        val userId = context.session.account?.id.orEmpty()
        val unreadNotices = notifications.count {
            it.category == NotificationCategory.NOTICE && !MemberNotifications.isRead(userId, it.id)
        }
        val pendingAssignments = notifications.count {
            it.category == NotificationCategory.ASSIGNMENT && !MemberNotifications.isRead(userId, it.id)
        }

        return """
            <h1 class="eb-page-title">${greeting(context)}</h1>
            ${statusCard(checkIn, checkOut)}
            ${nextClassCard(next)}
            <div class="eb-quick-links">
                ${academyApplicationEntry(context)}
                <a class="eb-list-row" href="/app/me/notices">${icon("icon-notice", "md")}읽지 않은 공지 <span class="eb-badge-count">$unreadNotices</span></a>
                <a class="eb-list-row" href="/app/me/board">${icon("icon-notice", "md")}학원 게시판${icon("icon-chevron-right", "xs")}</a>
                <a class="eb-list-row" href="/app/me/assignments">${icon("icon-assignment", "md")}처리할 과제 <span class="eb-badge-count">$pendingAssignments</span></a>
            </div>
        """.trimIndent()
    }

    private fun greeting(context: MemberContext): String {
        val hour = Date().getHours()
        val part = when {
            hour < 12 -> "좋은 아침이에요"
            hour < 18 -> "좋은 오후예요"
            else -> "좋은 저녁이에요"
        }
        return "$part, ${context.subjectDisplayName().escapeHtml()} 님"
    }

    private fun academyApplicationEntry(context: MemberContext): String {
        if (context.session.activeMembership != null || context.session.activePermission != Permission.VIEWER) return ""
        return """
            <a class="eb-list-row" href="/app/me/academy-application">${icon("icon-academy", "md")}학원 시작하기${icon("icon-chevron-right", "xs")}</a>
        """.trimIndent()
    }

    private fun statusCard(checkIn: AttendanceTimelineEntry?, checkOut: AttendanceTimelineEntry?): String {
        val (headline, caption, headlineIcon) = when {
            checkOut != null -> Triple("하원 완료", "오늘도 잘 다녀왔어요", "icon-check-out")
            checkIn != null -> Triple("수업 중", "즐겁게 수업 듣고 있어요", "icon-check-in")
            else -> Triple("등원 전", "아직 등원 기록이 없어요", "icon-calendar")
        }
        val checkInStep = timelineStep("등원", "icon-academy", checkIn?.occurredAt, done = checkIn != null, current = false)
        val classStep = timelineStep(
            "수업",
            "icon-calendar",
            occurredAtIso = null,
            done = checkOut != null,
            current = checkIn != null && checkOut == null,
        )
        val checkOutStep = timelineStep("하원", "icon-check-out", checkOut?.occurredAt, done = checkOut != null, current = false)

        return """
            <section class="eb-today-status-card">
                ${icon(headlineIcon, "xl")}
                <p class="eb-meta">$caption</p>
                <h2>$headline</h2>
                <ol class="eb-timeline">$checkInStep$classStep$checkOutStep</ol>
                ${if (checkOut != null) """<p class="eb-meta">보호자에게 하원 알림을 보냈어요.</p>""" else ""}
            </section>
        """.trimIndent()
    }

    private fun timelineStep(label: String, iconId: String, occurredAtIso: String?, done: Boolean, current: Boolean): String {
        val modifier = when {
            done -> "eb-timeline__step--done"
            current -> "eb-timeline__step--current"
            else -> "eb-timeline__step--pending"
        }
        val time = occurredAtIso?.let { """<time>${formatKstClockTime(it)}</time>""" }.orEmpty()
        return """<li class="eb-timeline__step $modifier">${icon(iconId, "md")}<span>$label</span>$time</li>"""
    }

    private fun nextClassCard(next: Pair<ClassData, String>?): String {
        if (next == null) return ""
        val (classData, label) = next
        return """
            <section class="eb-card eb-next-class">
                <h2>다음 수업</h2>
                <a class="eb-list-row" href="/app/me/schedule">
                    ${icon("icon-calendar", "md")}
                    <span>$label · ${classData.subject.escapeHtml()}</span>
                    ${icon("icon-chevron-right", "xs")}
                </a>
            </section>
        """.trimIndent()
    }
}
